import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import type { PilloryPost } from '@/types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Target, Clock, ThumbsUp, Users, Share2, Check, LogIn } from 'lucide-react';
import { trackEvent } from '@/lib/faro';

function timeRemaining(expiresAt: string): string {
    const diff = new Date(expiresAt).getTime() - Date.now();
    if (diff <= 0) return 'Expired';
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    if (hours > 0) return `${hours}h ${minutes}m left`;
    return `${minutes}m left`;
}

export default function PillorySharePage() {
    const { token } = useParams<{ token: string }>();
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const [copied, setCopied] = useState(false);

    const { data: post, isLoading, error } = useQuery({
        queryKey: ['pillory-share', token],
        queryFn: () => api.get<{ data: PilloryPost }>(`/pillory/share/${token}`).then(r => r.data),
        enabled: !!token,
    });

    const voteMutation = useMutation({
        mutationFn: () => api.post<{ vote_count: number; total_added: string }>(`/pillory/${post!.id}/vote`, {}),
        onSuccess: () => {
            trackEvent('pillory_voted', { source: 'share_link' });
            queryClient.invalidateQueries({ queryKey: ['pillory-share', token] });
        },
    });

    const handleShare = () => {
        const url = window.location.href;
        const copy = navigator.clipboard
            ? navigator.clipboard.writeText(url)
            : new Promise<void>((resolve) => {
                  const ta = document.createElement('textarea');
                  ta.value = url;
                  ta.style.position = 'fixed';
                  ta.style.opacity = '0';
                  document.body.appendChild(ta);
                  ta.select();
                  document.execCommand('copy');
                  document.body.removeChild(ta);
                  resolve();
              });
        copy.then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    if (isLoading) {
        return <p className="text-center text-sm text-slate-500">Loading...</p>;
    }

    if (error || !post) {
        return (
            <div className="text-center space-y-4">
                <p className="text-sm text-red-400">Pillory post not found.</p>
                <Link to="/" className="text-sm text-violet-400 hover:underline">
                    Go to DebtBound
                </Link>
            </div>
        );
    }

    const canVote = post.is_active && (
        user
            ? (post.user_votes ?? 0) < post.max_votes_per_user
            : !post.require_login
    );
    const isParticipant = user && (user.id === post.dom?.id || user.id === post.sub?.id);

    return (
        <div className="mx-auto max-w-xl space-y-6">
            <div className="text-center">
                <h1 className="flex items-center justify-center gap-2 text-2xl font-bold text-orange-400">
                    <Target className="h-6 w-6" />
                    The Pillory
                    <Target className="h-6 w-6" />
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                    Someone shared this pillory post with you
                </p>
            </div>

            <Card className="border-orange-900/30">
                <CardContent className="py-4">
                    <div className="flex items-start gap-3">
                        {post.sub?.avatar_path ? (
                            <img
                                src={post.sub.avatar_path}
                                alt={post.sub.display_name}
                                className="h-10 w-10 shrink-0 rounded-full object-cover"
                            />
                        ) : (
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-900/30 text-orange-400 font-bold">
                                {post.sub?.display_name?.[0]?.toUpperCase() ?? '?'}
                            </div>
                        )}

                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-orange-400">
                                    {post.sub?.display_name ?? 'Unknown'}
                                </span>
                                <span className="text-sm text-slate-500">posted to the pillory by</span>
                                <span className="text-sm text-violet-400">
                                    {post.dom?.display_name ?? 'Unknown'}
                                </span>
                            </div>

                            <p className="mt-1 text-sm text-slate-300 italic">"{post.reason}"</p>

                            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                                <Badge variant="outline" className="gap-1 border-orange-800 text-orange-400">
                                    <ThumbsUp className="h-3 w-3" />
                                    {post.vote_count} vote{post.vote_count !== 1 ? 's' : ''}
                                </Badge>
                                <span className="text-slate-400">
                                    ${parseFloat(post.fee_per_vote).toFixed(2)}/vote
                                </span>
                                <span className="text-red-400 font-medium">
                                    ${parseFloat(post.total_added).toFixed(2)} added
                                </span>
                                <span className="flex items-center gap-1 text-slate-500">
                                    <Clock className="h-3 w-3" />
                                    {timeRemaining(post.expires_at)}
                                </span>
                                {post.max_votes_per_user > 1 && (
                                    <span className="flex items-center gap-1 text-slate-500">
                                        <Users className="h-3 w-3" />
                                        max {post.max_votes_per_user}/user
                                    </span>
                                )}
                            </div>

                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                {post.is_active && !isParticipant && (
                                    (user || !post.require_login) ? (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="border-orange-800 text-orange-400 hover:bg-orange-900/30"
                                            disabled={!canVote || voteMutation.isPending}
                                            onClick={() => voteMutation.mutate()}
                                        >
                                            {voteMutation.isPending
                                                ? 'Voting...'
                                                : !canVote
                                                  ? 'Max votes reached'
                                                  : `Vote (+$${parseFloat(post.fee_per_vote).toFixed(2)})`}
                                        </Button>
                                    ) : (
                                        <>
                                            <Button
                                                size="sm"
                                                asChild
                                                className="bg-violet-600 hover:bg-violet-700"
                                            >
                                                <Link to="/login" className="whitespace-nowrap">
                                                    <LogIn className="h-3.5 w-3.5 mr-1 inline" />
                                                    Sign in to vote
                                                </Link>
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                asChild
                                                className="text-slate-400 hover:text-slate-200"
                                            >
                                                <Link to="/register">
                                                    Create account
                                                </Link>
                                            </Button>
                                        </>
                                    )
                                )}
                                {!post.is_active && (
                                    <Badge variant="secondary" className="text-[10px]">
                                        Expired
                                    </Badge>
                                )}
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-slate-400 hover:text-slate-200"
                                    onClick={handleShare}
                                >
                                    {copied ? <Check className="h-3.5 w-3.5 mr-1" /> : <Share2 className="h-3.5 w-3.5 mr-1" />}
                                    {copied ? 'Copied!' : 'Share'}
                                </Button>
                            </div>
                            {voteMutation.isError && (
                                <p className="mt-1 text-xs text-red-400">
                                    {voteMutation.error instanceof ApiError
                                        ? voteMutation.error.message
                                        : 'Failed to vote'}
                                </p>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
